import React, { useState, useEffect, useCallback } from "react";
import "./GameTutorial.css";
import { getAuth } from "firebase/auth";

async function markTutorialCompleteOnServer() {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    console.error("No authenticated session located.");
    return { success: false, message: "No active session." };
  }

  try {
    const token = await currentUser.getIdToken(true);
    const cloudFunctionUrl = "https://us-central1-stockmarket-83a73.cloudfunctions.net/markTutorialComplete";

    const response = await fetch(cloudFunctionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Network interface error updating tutorial status:", error);
    return { success: false, message: error.message };
  }
}

const steps = [
  {
    name: "Welcome",
    type: "full-screen",
    title: "Welcome to YieldNet",
    color: "#50E3C2",
    text: "Welcome to your trading dashboard. This onboarding walkthrough will familiarize you with the core layout and account management features of <span style='color: #50E3C2; font-weight: bold;'>OutTrade Online</span>.",
  },
  {
    name: "Account",
    type: "spotlight",
    selector: "#username",
    title: "Account Overview",
    color: "#50E3C2",
    text: "This <span style='color: #50E3C2; font-weight: bold;'>Account Overview</span> console displays your trader credentials, total portfolio value (including cash), wallet balance (USD), and cumulative experience accrual. You can also view system time (UTC), current client version, and manage account preferences via the settings menu.",
  },
  {
    name: "Portfolio",
    type: "spotlight",
    selector: "#portfolio",
    title: "Portfolio Management",
    color: "#50E3C2",
    text: "The <span style='color: #50E3C2; font-weight: bold;'>Portfolio Management</span> component is your centralized hub for tracking all open positions across the market. Use the tabs to check your primary portfolio holdings, manage your public trader profile, view transaction history, view any held bonds & futures, monitor leaderboard rankings, and view achievements.",
  },
  {
    name: "Navigation",
    type: "spotlight",
    selector: "#player-search",
    title: "Navigation Bar",
    color: "#50E3C2",
    text: "The <span style='color: #50E3C2; font-weight: bold;'>Navigation Bar</span> is your quick-access utility panel. From left to right, you can browse game development news, access the global and personal assets database, use the trader lookup bar, view the latest currency exchange rates, check macro market indices, and manage your referrals.",
  },
  {
    name: "DataStream",
    type: "spotlight",
    selector: "#datastream",
    title: "Live Transaction DataStream",
    color: "#50E3C2",
    text: "Click <span style='color: #50E3C2; font-weight: bold;'>Live Transaction DataStream</span> to monitor real-time transactions across the system. This module also features the public message wall for broad communications, the Signal EXP mini-game, and access to work for short-term passive earnings.",
  },
  {
    name: "Notifications",
    type: "spotlight",
    selector: "#notifications",
    title: "Notifications & Alerts",
    color: "#50E3C2",
    text: "The <span style='color: #50E3C2; font-weight: bold;'>Notifications & Alerts</span> panel logs automated system triggers and transaction confirmations. You can also unlock daily rewards and claim your assets here, and view and purchase tickets to the weekly jackpot.",
  },
  {
    name: "Communications",
    type: "spotlight",
    selector: "#inbox",
    title: "Secure Messaging Console",
    color: "#50E3C2",
    text: "A direct, private communication suite inside the <span style='color: #50E3C2; font-weight: bold;'>Secure Messaging Console</span>. Manage incoming inquiries via your inbox, track sent messages in the outbox, or compose direct messages to other traders.",
  },
  {
    name: "Chat",
    type: "spotlight",
    selector: ".v2-chat-anchored-wrapper, #v2-chat-anchored-wrapper, [class*='chat']",
    title: "Communication Channels",
    color: "#50E3C2",
    text: "Engage with other traders across dedicated, real-time <span style='color: #50E3C2; font-weight: bold;'>Communication Channels</span>. Keep an eye on the 'System' channel for important corporate announcements, IPOs, and other market updates.",
  },
  {
    name: "Market Browser",
    type: "spotlight",
    selector: "#market-browser",
    title: "Global Market Browser",
    color: "#50E3C2",
    text: "The <span style='color: #50E3C2; font-weight: bold;'>Global Market Browser</span> is the directory for all available securities. Navigate financial instruments by total market, trader-owned or non-trader-owned companies, stocks, cryptocurrency, foreign exchange currencies, YieldNet benchmarks, your personal watchlist, and the asset exchange. Click any listed security to load its data, then click Continue.",
  },
  {
    name: "Company Profile",
    type: "spotlight",
    selector: ".v2-profile-outer-container, [class*='profile-outer']",
    title: "Company Profile Panel",
    color: "#50E3C2",
    text: "Analyze fundamental and technical data on the <span style='color: #50E3C2; font-weight: bold;'>Company Profile Panel</span>. Toggle the 'Profile' tab to evaluate company details and purchase shares, click 'Charts' to view historical price movements and indicators, and inspect 'Shareholders' to view the up-to-date share distribution list. Click Continue when ready.",
  },
  {
    name: "Authorized",
    type: "full-screen",
    title: "Authentication Success",
    color: "#F4C542",
    text: "Your trading terminal is fully synchronized and operational. You are now authorized to access <span style='color: #50E3C2; font-weight: bold;'>YieldNet</span>.",
  }
];

export default function GameTutorial({ isActive, onComplete, isUserLoading, isTutorialActive }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [clipCoords, setClipCoords] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentData = steps[currentStep];

  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
      setClipCoords(null);
    }
  }, [isActive]);

  const updateSpotlight = useCallback(() => {
    const currentData = steps[currentStep];

    if (!isActive || currentStep >= steps.length || currentData?.type === "full-screen") {
      setClipCoords(null);
      return;
    }

    const selectors = currentData?.selector?.split(", ") || [];
    let element = null;
    
    for (let selector of selectors) {
      element = document.querySelector(selector);
      if (element) break;
    }

    if (element) {
      const rect = element.getBoundingClientRect();
      const padding = 8;

      setClipCoords({
        t: rect.top - padding,
        l: rect.left - padding,
        b: rect.bottom + padding,
        r: rect.right + padding,
        width: rect.width + (padding * 2),
        height: rect.height + (padding * 2)
      });
    } else {
      console.warn(`Tutorial step missing targeted HTML element: ${currentData?.name}`);
      setClipCoords(null);
    }
  }, [currentStep, isActive]);

  useEffect(() => {
    updateSpotlight();

    window.addEventListener("resize", updateSpotlight);
    window.addEventListener("scroll", updateSpotlight);

    let observer = null;
    let animationFrameId = null;

    if (isActive && currentStep < steps.length) {
      const selectors = steps[currentStep].selector?.split(", ");
      let targetElement = null;

      if (selectors) {
        for (let selector of selectors) {
          targetElement = document.querySelector(selector);
          if (targetElement) break;
        }
      }

      if (targetElement) {
        const smoothTrackLoop = () => {
          updateSpotlight();
          animationFrameId = requestAnimationFrame(smoothTrackLoop);
        };

        observer = new MutationObserver(() => {
          updateSpotlight();

          if (!animationFrameId) {
            smoothTrackLoop();
            setTimeout(() => {
              if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
              }
            }, 500);
          }
        });

        observer.observe(targetElement, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ["class", "style"]
        });
      }
    }
    
    return () => {
      window.removeEventListener("resize", updateSpotlight);
      window.removeEventListener("scroll", updateSpotlight);
      if (observer) observer.disconnect();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [currentStep, isActive, updateSpotlight]);

  if (!isActive || !currentData) return null;

  const clipPathString = clipCoords && currentData.type === "spotlight"
    ? `polygon(
        0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, 
        ${clipCoords.l}px ${clipCoords.t}px, 
        ${clipCoords.l}px ${clipCoords.b}px, 
        ${clipCoords.r}px ${clipCoords.b}px, 
        ${clipCoords.r}px ${clipCoords.t}px, 
        ${clipCoords.l}px ${clipCoords.t}px
      )`
    : "none";

  const handleNextClick = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(p => p + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await markTutorialCompleteOnServer();
      if (result && result.success) {
        console.log("Database status updated successfully.");
        onComplete();
      } else {
        alert(`Server Synchronization Failed: ${result?.message || "Unknown Failure"}`);
      }
    } catch (err) {
      console.error("Fatal transaction execution error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

if (isUserLoading) {
  return null;
}

if (!isTutorialActive) {
  return null;
}

  return (
    <div className="tutorial-global-root">
      <div 
        className={`tutorial-hollow-backdrop ${currentData.type === "full-screen" ? "is-full-blur" : ""}`}
        style={{ clipPath: clipPathString }}
      />

      {clipCoords && currentData.type === "spotlight" && (
        <div 
          className="tutorial-accent-ring"
          style={{
            top: `${clipCoords.t}px`,
            left: `${clipCoords.l}px`,
            width: `${clipCoords.width}px`,
            height: `${clipCoords.height}px`
          }}
        />
      )}

      <div 
        className={`tutorial-info-card ${currentData.type === "full-screen" ? "card-centered" : "card-anchored"}`}
        style={
          clipCoords && currentData.type === "spotlight"
            ? {
                top: `${Math.max(20, Math.min(window.innerHeight - 260, clipCoords.t + (clipCoords.height / 2) - 100))}px`,
                left: clipCoords.r + 360 < window.innerWidth
                  ? `${clipCoords.r + 12}px`
                  : `${Math.max(20, clipCoords.l - 352)}px`,
                transform: "none"
              }
            : undefined
        }
      >
        <span className="tutorial-step-tag">Step {currentStep + 1} of {steps.length}</span>
        <h3 className="tutorial-card-title">{currentData.title}</h3>
        <p 
  className="tutorial-card-body" 
  dangerouslySetInnerHTML={{ __html: currentData.text }} 
  style={{ color: "#fff" }}
/>
        
        <div className="tutorial-card-footer">
          <button 
            className="tutorial-action-btn" 
            onClick={handleNextClick}
            disabled={isSubmitting}
            style={isSubmitting ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            {isSubmitting 
              ? "LOADING..." 
              : currentStep === steps.length - 1 
                ? "INITIALIZE" 
                : "CONTINUE"
            }
          </button>
        </div>
      </div>
    </div>
  );
}